import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isWeekend, getDay, parseISO, isSameMonth } from 'date-fns';
import it from 'date-fns/locale/it';
import axios from 'axios';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Clock,
  Phone,
  Timer,
  Briefcase,
  Save,
  Loader2,
  Send,
  Receipt,
  CheckSquare,
  Square,
  Plus,
  Wand2,
  Trash2,
  Download,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { useNationalHolidays } from '../contexts/NationalHolidaysContext';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { useMobile } from '../hooks/use-mobile';
import { PageTransition } from '../components/PageTransition';
import { ApprovalStatusBadge } from '../components/ApprovalStatusBadge';
import { TimesheetState } from '../services/timesheetService';
import { orderAssignmentService, OrderAssignment } from '../services/orderAssignmentService';
import { DateRangeEntryDialog, TimesheetEntryData } from '../components/DateRangeEntryDialog';
import AutoFillDialog from '../components/AutoFillDialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ExpenseClaim, expenseClaimsService } from '../services/expenseClaimsService';
import { getApiBaseUrl } from '../lib/api-base-url';
import { formatDuration, hoursToMinutes, isOnCall, minutesToHours, sumHours } from '../lib/timesheet-hours';

const API_URL = getApiBaseUrl();

interface TimesheetEntry {
  id?: number;
  date: string;
  orderId: number;
  workedHours: number;
  /** On call that day, even with 0 hours (missing on entries saved before the flag existed) */
  onCall?: boolean;
  onCallHours: number;
  overtimeHours: number;
  workPermit: number;
  state?: TimesheetState;
  rejectionReason?: string;
  order?: {
    id: number;
    code: string;
    description: string;
  };
}

// Key for entries: date:orderId
const makeEntryKey = (date: string, orderId: number) => `${date}:${orderId}`;

/** On-call cell: the phone marks the day as on call, so "0:00" reads as "on call, no call-outs". */
const OnCallHours: React.FC<{ hours: number | string }> = ({ hours }) => {
  const { t } = useTranslation();
  return (
    <span
      className="inline-flex items-center gap-1 text-blue-700 dark:text-blue-300"
      title={t('timesheet.entry.onCall', 'On call')}
    >
      <Phone className="h-3 w-3" aria-hidden />
      {formatDuration(hours)}
    </span>
  );
};

/** One line under an entry on phones, where the table's columns are not shown. */
const EntryHoursBreakdown: React.FC<{ entry: TimesheetEntry }> = ({ entry }) => {
  const { t } = useTranslation();
  const parts = [
    t('timesheet.entry.recapOrdinary', '{{hours}} ordinary', { hours: formatDuration(entry.workedHours) }),
    Number(entry.overtimeHours) > 0 &&
      t('timesheet.entry.recapOvertime', '{{hours}} overtime', { hours: formatDuration(entry.overtimeHours) }),
    isOnCall(entry) &&
      t('timesheet.entry.recapOnCall', 'on call ({{hours}})', { hours: formatDuration(entry.onCallHours) }),
  ].filter(Boolean);
  return <div className="text-[11px] text-muted-foreground tabular-nums">{parts.join(' · ')}</div>;
};

/** Leave the backend attaches to a calendar day. Only an approved one is time off. */
type CalendarLeaveInfo = { type: string; durationType: string; hours?: number; state?: 'pending' | 'approved' };

/** `?month=yyyy-MM` → first day of that month; anything else → null. */
const parseMonthParam = (value: string | null): Date | null => {
  if (!value || !/^\d{4}-\d{2}$/.test(value)) return null;
  const [year, month] = value.split('-').map(Number);
  if (month < 1 || month > 12) return null;
  return new Date(year, month - 1, 1);
};

const MonthlyTimesheet: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const isMobile = useMobile();
  const { isHoliday, isBlockingHoliday } = useNationalHolidays();
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const dateLocale = i18n.language?.startsWith('it') ? it : undefined;

  // The month starts from the URL (?month=yyyy-MM), so the first fetch already asks for the right
  // one; the effect further down keeps it in sync with later URL changes.
  const [selectedDate, setSelectedDate] = useState(() => parseMonthParam(searchParams.get('month')) ?? new Date());

  // Entries keyed by date:orderId
  const [entries, setEntries] = useState<Record<string, TimesheetEntry>>({});
  const [leaveMap, setLeaveMap] = useState<Record<string, CalendarLeaveInfo>>({});
  const [unsavedEntries, setUnsavedEntries] = useState<Record<string, TimesheetEntry>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedEntryIds, setSelectedEntryIds] = useState<Set<number>>(new Set());
  const [monthlyExpenseClaims, setMonthlyExpenseClaims] = useState<ExpenseClaim[]>([]);

  // Order assignments
  const [availableOrders, setAvailableOrders] = useState<OrderAssignment[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [ordersLoadFailed, setOrdersLoadFailed] = useState(false);

  // Modal state for cell editing
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCell, setSelectedCell] = useState<{ date: string; orderId: number } | null>(null);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');

  // Expand/collapse state for day rows
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());

  // Auto-fill dialog state
  const [autoFillOpen, setAutoFillOpen] = useState(false);
  const [saveErrorEntryKeys, setSaveErrorEntryKeys] = useState<Set<string>>(new Set());
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [absenceDetail, setAbsenceDetail] = useState<(CalendarLeaveInfo & { date: string }) | null>(null);

  const getLeaveTypeLabel = useCallback((type: string) => {
    return t(`leave.types.${type}`, type);
  }, [t]);

  const getLeaveDurationLabel = useCallback((durationType: string) => {
    return t(`leave.duration.${durationType}`, durationType);
  }, [t]);

  /** Badge text for a day's leave; a request still waiting for approval says so. */
  const getLeaveBadgeLabel = useCallback((leave: CalendarLeaveInfo) => {
    const typeLabel = getLeaveTypeLabel(leave.type);
    return leave.state === 'pending' ? `${typeLabel} · ${t('timesheet.leavePending', 'in attesa')}` : typeLabel;
  }, [getLeaveTypeLabel, t]);

  const getBaseLeaveHours = useCallback((leave: { durationType: string; hours?: number }) => {
    const explicitHours = Number(leave.hours ?? 0);
    if (explicitHours > 0) return explicitHours;

    if (leave.durationType === 'full_day') return 8;
    if (leave.durationType === 'half_day_morning' || leave.durationType === 'half_day_afternoon') return 4;
    return 0;
  }, []);

  const daysInMonth = useMemo(() => {
    const start = startOfMonth(selectedDate);
    const end = endOfMonth(selectedDate);
    return eachDayOfInterval({ start, end });
  }, [selectedDate]);

  const hasUnsavedChanges = Object.keys(unsavedEntries).length > 0;

  const getEffectiveLeaveHoursForDate = useCallback((dateStr: string) => {
    const leave = leaveMap[dateStr];
    if (!leave) return 0;
    // A pending request is shown on the day but is not time off yet. Like the backend (save-time
    // checks, management issues) only approved leave counts as hours.
    if (leave.state !== 'approved') return 0;

    const rawLeaveHours = getBaseLeaveHours(leave);
    if (rawLeaveHours <= 0) return 0;

    const date = parseISO(dateStr);
    const holiday = isHoliday(date);
    const blockingHoliday = isBlockingHoliday(date);

    // If this holiday explicitly allows timesheet entries, leave does not lock worked hours.
    if (holiday && !blockingHoliday) {
      return 0;
    }

    return rawLeaveHours;
  }, [getBaseLeaveHours, isBlockingHoliday, isHoliday, leaveMap]);

  // Get all draft entries that can be submitted
  const draftEntries = useMemo(() => {
    return Object.values(entries).filter(
      (entry) => entry.id && (!entry.state || entry.state === 'draft')
    );
  }, [entries]);

  const approvedHoursByDate = useMemo(() => {
    const hoursMap = new Map<string, number>();
    for (const entry of Object.values(entries)) {
      if (entry.state !== 'approved') continue;
      const totalHours = Number(entry.workedHours || 0) + Number(entry.onCallHours || 0) + Number(entry.overtimeHours || 0);
      if (totalHours <= 0) continue;
      const dateKey = String(entry.date).split('T')[0];
      hoursMap.set(dateKey, (hoursMap.get(dateKey) ?? 0) + totalHours);
    }
    return hoursMap;
  }, [entries]);

  const monthHasCompleteCoverage = useMemo(() => {
    return daysInMonth.every((day) => {
      if (isWeekend(day)) return true;

      const dateKey = format(day, 'yyyy-MM-dd');
      if (isBlockingHoliday(day)) return true;
      if (getEffectiveLeaveHoursForDate(dateKey) > 0) return true;
      return (approvedHoursByDate.get(dateKey) ?? 0) > 0;
    });
  }, [approvedHoursByDate, daysInMonth, getEffectiveLeaveHoursForDate, isBlockingHoliday]);

  const monthIsApproved = useMemo(() => {
    const monthEntries = Object.values(entries);
    return monthEntries.length > 0
      && monthEntries.every((entry) => entry.state === 'approved')
      && monthHasCompleteCoverage;
  }, [entries, monthHasCompleteCoverage]);

  const isReadOnlyMode = monthIsApproved;

  // Nothing to log hours against: every add affordance must be turned off.
  // This covers a failed load too, where the list is empty but unknown.
  const cannotAddEntries = availableOrders.length === 0;

  // Narrower: the list really loaded and really is empty, so the user is
  // genuinely unassigned. Drives the explanatory banner only.
  const hasNoAssignedOrders = !loadingOrders && !ordersLoadFailed && cannotAddEntries;

  const allDraftsSelected = draftEntries.length > 0 &&
    draftEntries.every((entry) => entry.id && selectedEntryIds.has(entry.id));

  const toggleEntrySelection = (entryId: number) => {
    setSelectedEntryIds((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  };

  const toggleAllDrafts = () => {
    if (allDraftsSelected) {
      setSelectedEntryIds(new Set());
    } else {
      setSelectedEntryIds(new Set(draftEntries.map((e) => e.id!)));
    }
  };

  const handleSubmitForApproval = async () => {
    if (selectedEntryIds.size === 0) return;

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/timesheets/bulk-submit`, {
        ids: Array.from(selectedEntryIds),
      });
      const { succeeded, failed } = response.data as { succeeded: any[]; failed: { id: number; reason: string }[] };

      if (failed.length === 0) {
        showNotification(t('timesheet.notifications.submitted', 'Submitted for approval'), 'success');
        setSelectedEntryIds(new Set());
      } else if (succeeded.length === 0) {
        showNotification(t('timesheet.notifications.bulkSubmitAllFailed', 'Failed to submit entries for approval'), 'error');
      } else {
        showNotification(
          t('timesheet.notifications.bulkSubmitPartial', '{{success}} submitted, {{failed}} could not be submitted', {
            success: succeeded.length,
            failed: failed.length,
          }),
          'warning',
        );
        // Keep only the failed entries selected so the user can retry or investigate
        setSelectedEntryIds(new Set(failed.map((f) => f.id)));
      }

      fetchMonthlyData();
    } catch (error) {
      console.error('Error submitting for approval:', error);
      showNotification(t('timesheet.notifications.submitError', 'Failed to submit'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedEntryIds.size === 0) return;

    setDeleting(true);
    try {
      const response = await axios.post(`${API_URL}/timesheets/bulk-delete`, {
        ids: Array.from(selectedEntryIds),
      });
      const { succeeded, failed } = response.data as { succeeded: number[]; failed: { id: number; reason: string }[] };

      if (failed.length === 0) {
        showNotification(t('timesheet.notifications.deleted', 'Entries deleted'), 'success');
        setSelectedEntryIds(new Set());
      } else if (succeeded.length === 0) {
        showNotification(t('timesheet.notifications.bulkDeleteAllFailed', 'Failed to delete entries'), 'error');
      } else {
        showNotification(
          t('timesheet.notifications.bulkDeletePartial', '{{success}} deleted, {{failed}} could not be deleted', {
            success: succeeded.length,
            failed: failed.length,
          }),
          'warning',
        );
        setSelectedEntryIds(new Set(failed.map((f) => f.id)));
      }

      fetchMonthlyData();
    } catch (error) {
      console.error('Error deleting entries:', error);
      showNotification(t('timesheet.notifications.deleteError', 'Failed to delete'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleModalDelete = async () => {
    if (!selectedCell) return;
    const key = makeEntryKey(selectedCell.date, selectedCell.orderId);
    const entry = unsavedEntries[key] || entries[key];

    if (entry?.id) {
      await axios.post(`${API_URL}/timesheets/bulk-delete`, { ids: [entry.id] });
      fetchMonthlyData();
    } else {
      // Purely unsaved — remove from local state only
      setEntries((prev) => { const next = { ...prev }; delete next[key]; return next; });
      setUnsavedEntries((prev) => { const next = { ...prev }; delete next[key]; return next; });
    }

    setModalOpen(false);
    setSelectedCell(null);
    showNotification(t('timesheet.notifications.deleted', 'Entries deleted'), 'success');
  };

  const isEntryEditable = (entry: TimesheetEntry) => {
    if (monthIsApproved) return false;
    return !entry.state || entry.state === 'draft' || entry.state === 'rejected' || entry.state === 'in_approval';
  };

  const getDayName = (date: Date, short = false) => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayIndex = getDay(date);
    return t(short ? `calendar.shortDays.${days[dayIndex]}` : `calendar.days.${days[dayIndex]}`);
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setSelectedDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + (direction === 'next' ? 1 : -1));
      return newDate;
    });
  };

  // A response that belongs to a superseded request (the month changed meanwhile) must not
  // overwrite the view: the sequence number tells each request whether it is still the current one.
  const monthlyRequestSeq = useRef(0);
  const ordersRequestSeq = useRef(0);

  const fetchMonthlyData = useCallback(async () => {
    const requestId = ++monthlyRequestSeq.current;
    const isCurrent = () => requestId === monthlyRequestSeq.current;

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/timesheets/monthly?year=${selectedDate.getFullYear()}&month=${selectedDate.getMonth() + 1}`
      );
      if (!isCurrent()) return;
      // Backend may return MonthlyCalendarEntry[] with { date, timesheets: [], leave? }
      const entriesMap: Record<string, TimesheetEntry> = {};
      const newLeaveMap: Record<string, CalendarLeaveInfo> = {};

      if (Array.isArray(response.data) && response.data.length > 0 && response.data[0].timesheets !== undefined) {
        for (const calEntry of response.data) {
          if (calEntry.leave) newLeaveMap[calEntry.date] = calEntry.leave;
          for (const ts of calEntry.timesheets) {
            const key = makeEntryKey(calEntry.date, ts.orderId);
            entriesMap[key] = ts;
          }
        }
      } else {
        // Fallback: older shape (array of timesheet entries)
        for (const entry of response.data) {
          const key = makeEntryKey(entry.date, entry.orderId);
          entriesMap[key] = entry;
        }
      }

      setEntries(entriesMap);
      setLeaveMap(newLeaveMap);
      setUnsavedEntries({});
    } catch (error) {
      if (isCurrent()) console.error('Error fetching timesheet data:', error);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchMonthlyData();
  }, [fetchMonthlyData]);

  // The URL is the source of truth: arriving with ?month= shows that month, and navigating to the
  // bare page (e.g. from the menu) brings the view back to the current month.
  useEffect(() => {
    const target = parseMonthParam(searchParams.get('month')) ?? new Date();
    setSelectedDate((prev) => (isSameMonth(prev, target) ? prev : target));
  }, [searchParams]);

  // Fetch the orders assigned to me
  const fetchUserOrders = useCallback(async () => {
    const targetId = user?.id;
    if (!targetId) return;
    const requestId = ++ordersRequestSeq.current;
    const isCurrent = () => requestId === ordersRequestSeq.current;
    setLoadingOrders(true);
    try {
      const assignments = await orderAssignmentService.getByUser(targetId);
      if (!isCurrent()) return;
      const activeAssignments = assignments.filter((a) => a.isActive && a.order);
      setAvailableOrders(activeAssignments);
      setOrdersLoadFailed(false);
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Error fetching user orders:', error);
      // Keep the two cases apart: a failed request must not be reported as
      // "you have no orders", which sends the user to the wrong person.
      setOrdersLoadFailed(true);
    } finally {
      if (isCurrent()) setLoadingOrders(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchUserOrders();
  }, [fetchUserOrders]);

  const assignmentByOrderId = useMemo(() => {
    const map = new Map<number, OrderAssignment>();
    for (const assignment of availableOrders) {
      map.set(assignment.orderId, assignment);
    }
    return map;
  }, [availableOrders]);

  const isOrderAssignedOnDate = useCallback((orderId: number, dateStr: string) => {
    const assignment = assignmentByOrderId.get(orderId);
    if (!assignment) return false;

    const assignmentStart = String(assignment.startDate).split('T')[0];
    const assignmentEnd = assignment.endDate ? String(assignment.endDate).split('T')[0] : null;

    if (dateStr < assignmentStart) return false;
    if (assignmentEnd && dateStr > assignmentEnd) return false;
    return true;
  }, [assignmentByOrderId]);

  useEffect(() => {
    const fetchMonthlyExpenseClaims = async () => {
      try {
        const claims = await expenseClaimsService.getMine();
        const monthStart = startOfMonth(selectedDate);
        const monthEnd = endOfMonth(selectedDate);

        const inMonthClaims = claims.filter((claim) => {
          const claimDate = new Date(claim.date);
          return claimDate >= monthStart && claimDate <= monthEnd;
        });

        setMonthlyExpenseClaims(inMonthClaims);
      } catch (error) {
        console.error('Error fetching monthly expense claims:', error);
        showNotification(t('expenseClaims.messages.fetchError', 'Failed to load expense claims'), 'error');
      }
    };

    fetchMonthlyExpenseClaims();
  }, [selectedDate, showNotification, t]);

  // Calculate daily totals (sum of workedHours across all orders for a day)
  const dailyTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const entry of Object.values(entries)) {
      const dateStr = entry.date;
      totals[dateStr] = (totals[dateStr] || 0) + Number(entry.workedHours || 0);
    }
    // Also include unsaved entries
    for (const entry of Object.values(unsavedEntries)) {
      const dateStr = entry.date;
      const key = makeEntryKey(entry.date, entry.orderId);
      // If this is an update, subtract the original and add the new
      const original = entries[key];
      if (original) {
        totals[dateStr] = (totals[dateStr] || 0) - Number(original.workedHours || 0) + Number(entry.workedHours || 0);
      } else {
        totals[dateStr] = (totals[dateStr] || 0) + Number(entry.workedHours || 0);
      }
    }
    // Include approved leave hours for each date (reduce remaining available hours)
    for (const d of Object.keys(leaveMap)) {
      totals[d] = (totals[d] || 0) + getEffectiveLeaveHoursForDate(d);
    }
    return totals;
  }, [entries, getEffectiveLeaveHoursForDate, leaveMap, unsavedEntries]);

  const parseAssignmentErrors = (error: unknown): Array<{ orderId: number; date: string }> => {
    if (!axios.isAxiosError(error)) return [];

    const payload = error.response?.data as
      | { message?: string | string[]; failures?: Array<{ orderId: number; date: string }> }
      | undefined;
    const failures = payload?.failures;

    // New structured format: { failures: [{ orderId, date }] }
    if (Array.isArray(failures) && failures.length > 0) {
      return failures;
    }

    // Legacy single-entry message format
    const message = payload?.message;
    const msg = Array.isArray(message) ? message[0] || '' : message || error.message || '';
    const match = (msg as string).match(/order\s+(\d+)\s+for date\s+(\d{4}-\d{2}-\d{2})/i);
    if (match) {
      return [{ orderId: Number(match[1]), date: match[2] }];
    }

    return [];
  };

  const toggleDayExpanded = (dateStr: string) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dateStr)) {
        next.delete(dateStr);
      } else {
        next.add(dateStr);
      }
      return next;
    });
  };

  // Handle cell click - open modal for editing an existing entry
  const handleCellClick = (dateStr: string, orderId: number) => {
    setSelectedCell({ date: dateStr, orderId });
    setModalMode('edit');
    setModalOpen(true);
  };

  // Open modal to add a new order entry on a specific day
  const handleAddOrderToDay = (dateStr: string) => {
    if (availableOrders.length === 0) {
      showNotification(
        t('timesheet.noOrdersAssigned', 'No orders assigned to you'),
        'info',
      );
      return;
    }

    const firstAvailableOrder = availableOrders.find(
      (a) => !(entriesByDate[dateStr] || []).some((e) => e.orderId === a.orderId)
    );
    setSelectedCell({ date: dateStr, orderId: firstAvailableOrder?.orderId || availableOrders[0]?.orderId });
    setModalMode('add');
    setModalOpen(true);
  };

  // Get the entry for the selected cell
  const getSelectedCellEntry = () => {
    if (!selectedCell) return null;
    const key = makeEntryKey(selectedCell.date, selectedCell.orderId);
    return unsavedEntries[key] || entries[key] || null;
  };

  // Memoised: the dialog resets its fields whenever this object changes, so a new one on every
  // render of the page would wipe what the user is typing.
  const selectedCellEntry = selectedCell
    ? unsavedEntries[makeEntryKey(selectedCell.date, selectedCell.orderId)] || entries[makeEntryKey(selectedCell.date, selectedCell.orderId)] || null
    : null;
  const dialogInitialEntry = useMemo(
    () => selectedCellEntry
      ? {
          workedHours: Number(selectedCellEntry.workedHours) || 0,
          onCall: isOnCall(selectedCellEntry),
          onCallHours: Number(selectedCellEntry.onCallHours) || 0,
          overtimeHours: Number(selectedCellEntry.overtimeHours) || 0,
          workPermit: Number(selectedCellEntry.workPermit) || 0,
        }
      : undefined,
    [selectedCellEntry],
  );

  // Handle modal submit
  const handleModalSubmit = async (modalEntries: TimesheetEntryData[]) => {
    const mergedEntries: Record<string, TimesheetEntry> = {
      ...entries,
      ...unsavedEntries,
    };
    const nextUpdates: Record<string, TimesheetEntry> = {};
    let movedToOvertimeMinutes = 0;

    // Overtime comes from the dialog. Ordinary hours that do not fit what is left of a day (leave,
    // other orders) are added to it: the dialog checks the selected day, a range spans many.
    for (const modalEntry of modalEntries) {
      const dateStr = modalEntry.date;
      const orderId = modalEntry.orderId || selectedCell?.orderId;
      if (!orderId) continue;

      const key = makeEntryKey(dateStr, orderId);
      const existing = mergedEntries[key];
      const otherEntriesOrdinaryMinutes = Object.values(mergedEntries)
        .filter((entry) => entry.date === dateStr && entry.orderId !== orderId)
        .reduce((sum, entry) => sum + hoursToMinutes(entry.workedHours), 0);

      const leaveHours = getEffectiveLeaveHoursForDate(dateStr);
      const ordinaryCapacityMinutes = hoursToMinutes(Math.max(0, 8 - leaveHours));
      const availableOrdinaryMinutes = Math.max(0, ordinaryCapacityMinutes - otherEntriesOrdinaryMinutes);
      const requestedOrdinaryMinutes = Math.max(0, hoursToMinutes(modalEntry.workedHours));
      const workedMinutes = Math.min(requestedOrdinaryMinutes, availableOrdinaryMinutes);
      const excessMinutes = requestedOrdinaryMinutes - workedMinutes;
      const workedHours = minutesToHours(workedMinutes);
      const overtimeHours = minutesToHours(hoursToMinutes(modalEntry.overtimeHours) + excessMinutes);

      const entry: TimesheetEntry = existing
        ? { ...existing }
        : {
            date: dateStr,
            orderId,
            workedHours: 0,
            onCallHours: 0,
            overtimeHours: 0,
            workPermit: 0,
          };

      // Update all fields
      entry.workedHours = workedHours;
      entry.onCall = modalEntry.onCall;
      entry.onCallHours = modalEntry.onCall ? modalEntry.onCallHours : 0;
      entry.overtimeHours = overtimeHours;
      entry.workPermit = modalEntry.workPermit;
      nextUpdates[key] = entry;
      mergedEntries[key] = entry;
      movedToOvertimeMinutes += excessMinutes;

      if (saveErrorEntryKeys.has(key)) {
        setSaveErrorEntryKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    }

    setEntries((prev) => ({ ...prev, ...nextUpdates }));
    setUnsavedEntries((prev) => ({ ...prev, ...nextUpdates }));

    if (movedToOvertimeMinutes > 0) {
      showNotification(
        t(
          'timesheet.info.overtimeAutoCalculated',
          'Hours above the daily limit ({{extra}}) were recorded as overtime.',
          { extra: formatDuration(minutesToHours(movedToOvertimeMinutes)) },
        ),
        'info',
      );
    }

    setModalOpen(false);
    setSelectedCell(null);
  };

  const handleSaveAll = async () => {
    setSaving(true);
    setSaveErrorEntryKeys(new Set());
    try {
      const entriesToSave = Object.values(unsavedEntries).map((entry) => ({
        date: entry.date,
        orderId: entry.orderId,
        workedHours: Number(entry.workedHours) || 0,
        onCall: isOnCall(entry),
        onCallHours: Number(entry.onCallHours) || 0,
        overtimeHours: Number(entry.overtimeHours) || 0,
        workPermit: Number(entry.workPermit) || 0,
      }));

      const year = format(selectedDate, 'yyyy');
      const month = format(selectedDate, 'MM');

      await axios.post(
        `${API_URL}/timesheets/batch/${year}/${month}`,
        { entries: entriesToSave }
      );

      showNotification(t('timesheet.notifications.saveSuccess', 'Timesheet saved successfully'), 'success');
      fetchMonthlyData();
    } catch (error: any) {
      const failures = parseAssignmentErrors(error);
      if (failures.length > 0) {
        const failingKeys = new Set(failures.map((f) => makeEntryKey(f.date, f.orderId)));
        setSaveErrorEntryKeys(failingKeys);
        setExpandedDays((prev) => {
          const next = new Set(prev);
          failures.forEach((f) => next.add(f.date));
          return next;
        });

        if (failures.length === 1) {
          const orderCode = availableOrders.find((a) => a.orderId === failures[0].orderId)?.order?.code;
          showNotification(
            t('timesheet.errors.assignmentOutOfRangeSave', {
              date: failures[0].date,
              order: orderCode || `#${failures[0].orderId}`,
            }),
            'error'
          );
        } else {
          showNotification(
            t('timesheet.errors.assignmentOutOfRangeMultiple', { count: failures.length }),
            'error'
          );
        }
      } else {
        showNotification(t('timesheet.notifications.saveError', 'Failed to save timesheet'), 'error');
      }

      console.error('Error saving timesheet:', error);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (saveErrorEntryKeys.size === 0 || isMobile) return;

    const firstKey = saveErrorEntryKeys.values().next().value as string;
    const timer = window.setTimeout(() => {
      const failingRow = document.querySelector(`[data-entry-key="${firstKey}"]`);
      if (failingRow instanceof HTMLElement) {
        failingRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);

    return () => window.clearTimeout(timer);
  }, [expandedDays, isMobile, saveErrorEntryKeys]);

  // Handle auto-fill month: generates entries for all weekdays (skipping weekends + holidays)
  const handleAutoFill = (orderId: number, hoursPerDay: number) => {
    const newUnsaved: Record<string, TimesheetEntry> = {};
    let filled = 0;

    for (const day of daysInMonth) {
      const dateStr = format(day, 'yyyy-MM-dd');
      const key = makeEntryKey(dateStr, orderId);

      // Skip weekends
      if (isWeekend(day)) continue;
      // Skip holidays
      if (isBlockingHoliday(day)) continue;
      // Skip days that already have an entry for this order
      if (entries[key] || unsavedEntries[key]) continue;
      // Skip days outside the selected order assignment range
      if (!isOrderAssignedOnDate(orderId, dateStr)) continue;

      // Check daily worked limit: existing hours on this day + new hours <= 8
      const existingDayWorked = dailyTotals[dateStr] || 0;
      const effectiveHours = Math.min(hoursPerDay, Math.max(0, 8 - existingDayWorked));
      if (effectiveHours <= 0) continue;

      newUnsaved[key] = {
        date: dateStr,
        orderId,
        workedHours: effectiveHours,
        onCall: false,
        onCallHours: 0,
        overtimeHours: 0,
        workPermit: 0,
      };
      filled++;
    }

    if (filled === 0) {
      showNotification(
        t('timesheet.autoFill.noDaysToFill', 'No available weekdays to fill for this order'),
        'info'
      );
      return;
    }

    setEntries((prev) => ({ ...prev, ...newUnsaved }));
    setUnsavedEntries((prev) => ({ ...prev, ...newUnsaved }));
    showNotification(
      t('timesheet.autoFill.filled', '{{count}} days filled with {{hours}}h', { count: filled, hours: hoursPerDay }),
      'success'
    );
  };

  const dailyExpenseTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const claim of monthlyExpenseClaims) {
      const dateStr = format(new Date(claim.date), 'yyyy-MM-dd');
      totals[dateStr] = (totals[dateStr] || 0) + Number(claim.amount || 0);
    }
    return totals;
  }, [monthlyExpenseClaims]);

  const monthlyExpenseTotal = useMemo(() => {
    return monthlyExpenseClaims.reduce((sum, claim) => sum + Number(claim.amount || 0), 0);
  }, [monthlyExpenseClaims]);

  const absenceStats = useMemo(() => {
    const stats = {
      totalHours: 0,
      vacationHours: 0,
      permissionHours: 0,
      sicknessHours: 0,
      rolHours: 0,
    };

    for (const [dateStr, leave] of Object.entries(leaveMap)) {
      if (!leave) continue;
      const leaveHours = getEffectiveLeaveHoursForDate(dateStr);
      if (leaveHours <= 0) continue;

      if (leave.type === 'vacation') {
        stats.vacationHours += leaveHours;
        stats.totalHours += leaveHours;
      } else if (leave.type === 'permission') {
        stats.permissionHours += leaveHours;
        stats.totalHours += leaveHours;
      } else if (leave.type === 'sickness') {
        stats.sicknessHours += leaveHours;
        stats.totalHours += leaveHours;
      } else if (leave.type === 'rol') {
        stats.rolHours += leaveHours;
        stats.totalHours += leaveHours;
      }
    }

    return stats;
  }, [getEffectiveLeaveHoursForDate, leaveMap]);

  const handleDownloadMonthlyPdf = async () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth() + 1;
    const params = new URLSearchParams({
      year: String(year),
      month: String(month),
    });

    try {
      setDownloadingPdf(true);
      const response = await axios.get(
        `${API_URL}/timesheets/download-pdf?${params.toString()}`,
        { responseType: 'blob' },
      );

      const blob = response.data as Blob;
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');

      const contentDisposition = String(response.headers?.['content-disposition'] || '');
      const utf8FileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
      const plainFileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      const parsedFileName = utf8FileNameMatch?.[1]
        ? decodeURIComponent(utf8FileNameMatch[1])
        : plainFileNameMatch?.[1];

      const safeUserName = (user?.name || 'user')
        .trim()
        .replace(/[^a-zA-Z0-9_-]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      const monthLabel = String(month).padStart(2, '0');

      anchor.href = url;
      anchor.download = parsedFileName || `timesheet-${safeUserName}-${year}-${monthLabel}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading monthly PDF:', error);
      showNotification(t('timesheet.errors.downloadPdf', 'Failed to download PDF'), 'error');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Get entries grouped by date for the day-row view
  const entriesByDate = useMemo(() => {
    const grouped: Record<string, TimesheetEntry[]> = {};

    // Combine saved and unsaved entries
    const allEntries = { ...entries };
    for (const [key, entry] of Object.entries(unsavedEntries)) {
      allEntries[key] = entry;
    }

    for (const entry of Object.values(allEntries)) {
      if (!grouped[entry.date]) {
        grouped[entry.date] = [];
      }
      // Avoid duplicates
      const existingIndex = grouped[entry.date].findIndex(e => e.orderId === entry.orderId);
      if (existingIndex >= 0) {
        grouped[entry.date][existingIndex] = entry;
      } else {
        grouped[entry.date].push(entry);
      }
    }

    return grouped;
  }, [entries, unsavedEntries]);

  const displayDayTotals = useMemo(() => {
    const totals: Record<string, { worked: number; onCall: number; overtime: number; permit: number; total: number; isOnCall: boolean }> = {};

    for (const date of daysInMonth) {
      const dateStr = format(date, 'yyyy-MM-dd');
      const dayEntries = entriesByDate[dateStr] || [];
      // Stored values, as saved and approved. The worked/overtime split is decided once, at save
      // time (entry dialog here, backend again), so this page, approvals and the PDF all agree.
      const leaveHours = getEffectiveLeaveHoursForDate(dateStr);
      const worked = sumHours(dayEntries.map((entry) => entry.workedHours));
      const overtime = sumHours(dayEntries.map((entry) => entry.overtimeHours));
      const onCall = sumHours(dayEntries.map((entry) => entry.onCallHours));
      const permit = sumHours([...dayEntries.map((entry) => entry.workPermit), leaveHours]);

      totals[dateStr] = {
        worked,
        onCall,
        overtime,
        permit,
        total: sumHours([worked, onCall, overtime, permit]),
        isOnCall: dayEntries.some(isOnCall),
      };
    }

    return totals;
  }, [daysInMonth, entriesByDate, getEffectiveLeaveHoursForDate]);

  const grandTotals = useMemo(() => {
    return Object.values(displayDayTotals).reduce(
      (acc, curr) => ({
        worked: acc.worked + curr.worked,
        onCall: acc.onCall + curr.onCall,
        overtime: acc.overtime + curr.overtime,
      }),
      { worked: 0, onCall: 0, overtime: 0 }
    );
  }, [displayDayTotals]);

  const onCallDaysCount = useMemo(
    () => Object.values(displayDayTotals).filter((day) => day.isOnCall).length,
    [displayDayTotals],
  );

  const monthlyPermitTotal = useMemo(() => {
    return Object.values(displayDayTotals).reduce((sum, day) => sum + day.permit, 0);
  }, [displayDayTotals]);

  // Day-based Grid View with sub-rows for multiple orders
  const renderGridView = () => {
    if (loadingOrders) {
      return (
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      );
    }

    // Always render calendar grid even if user has no orders assigned.
    // If there are no orders we show days and any leave info; add buttons requiring orders will be disabled.

    return (
      <div className="relative rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-muted/50">
                <th className="sticky left-0 z-20 px-3 py-2 text-left font-medium min-w-[50px] border-r bg-slate-100 dark:bg-slate-800">
                  {t('timesheet.columns.day', 'Day')}
                </th>
                <th className="px-3 py-2 text-left font-medium min-w-[80px] border-r">
                  {t('timesheet.columns.dayName', 'Date')}
                </th>
                <th className="px-3 py-2 text-left font-medium min-w-[150px] border-r">
                  {t('timesheet.order', 'Order')}
                </th>
                <th className="px-3 py-2 text-center font-medium min-w-[80px] border-r">
                  <div className="flex items-center justify-center gap-1">
                    <Clock className="h-3 w-3 text-orange-500" />
                    {t('timesheet.columns.workedHours')}
                  </div>
                </th>
                <th className="px-3 py-2 text-center font-medium min-w-[80px] border-r">
                  <div className="flex items-center justify-center gap-1">
                    <Phone className="h-3 w-3 text-blue-500" />
                    {t('timesheet.columns.onCallHours')}
                  </div>
                </th>
                <th className="px-3 py-2 text-center font-medium min-w-[80px] border-r">
                  <div className="flex items-center justify-center gap-1">
                    <Timer className="h-3 w-3 text-purple-500" />
                    {t('timesheet.columns.overtimeHours')}
                  </div>
                </th>
                <th className="px-3 py-2 text-center font-medium min-w-[80px] border-r">
                  <div className="flex items-center justify-center gap-1">
                    <Briefcase className="h-3 w-3 text-green-500" />
                    {t('timesheet.columns.workPermit')}
                  </div>
                </th>
                <th className="px-3 py-2 text-center font-medium min-w-[70px] border-r bg-orange-100 dark:bg-orange-900/50">
                  {t('timesheet.total', 'Total')}
                </th>
                <th className="px-3 py-2 text-center font-medium min-w-[50px]">
                  <Checkbox
                    checked={allDraftsSelected}
                    onCheckedChange={toggleAllDrafts}
                    disabled={draftEntries.length === 0}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 9 }).map((_, j) => (
                      <td key={j} className="px-3 py-2 border-r">
                        <Skeleton className="h-8 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <>
                  {daysInMonth.map((date) => {
                    const dateStr = format(date, 'yyyy-MM-dd');
                    const dayEntries = entriesByDate[dateStr] || [];
                    const holiday = isHoliday(date);
                    const blockingHoliday = isBlockingHoliday(date);
                    const weekend = isWeekend(date);
                    const dayNumber = format(date, 'd');
                    const leaveHours = getEffectiveLeaveHoursForDate(dateStr);

                    // Calculate day totals
                    const daySummary = displayDayTotals[dateStr] || { worked: 0, onCall: 0, overtime: 0, permit: 0, total: 0, isOnCall: false };
                    const dayWorkedTotal = daySummary.worked;
                    const dayOnCallTotal = daySummary.onCall;
                    const dayOvertimeTotal = daySummary.overtime;
                    const dayWorkPermitTotal = daySummary.permit;
                    const dayTotal = daySummary.total;
                    const hasAnyEntry = dayEntries.length > 0;

                    // Base row style for weekend/holiday
                    const baseRowClass = cn(
                      'border-t hover:bg-muted/30 transition-colors',
                      holiday && 'bg-red-50 dark:bg-red-950/30',
                      weekend && !holiday && 'bg-gray-50 dark:bg-gray-800/50'
                    );

                    const isExpanded = expandedDays.has(dateStr);
                    const usedOrderIds = dayEntries.map((e) => e.orderId);
                    const canAddMore = availableOrders.some((a) => !usedOrderIds.includes(a.orderId));

                    // Empty day — no entries
                    if (!hasAnyEntry) {
                      return (
                        <tr
                          key={dateStr}
                          className={cn(baseRowClass, !isReadOnlyMode && !cannotAddEntries && 'cursor-pointer')}
                          onClick={() => {
                            if (isReadOnlyMode || cannotAddEntries) return;
                            const orderId = availableOrders[0]?.orderId;
                            if (orderId) {
                              setSelectedCell({ date: dateStr, orderId });
                              setModalMode('add');
                              setModalOpen(true);
                            }
                          }}
                        >
                          <td className="sticky left-0 z-10 px-3 py-2 border-r bg-slate-50 dark:bg-slate-900 font-semibold">
                            {dayNumber}
                          </td>
                          <td className="px-3 py-2 border-r">
                            <div className="font-medium">{getDayName(date, true)}</div>
                            {(holiday || weekend) && (
                              <Badge variant="outline" className={cn(
                                'text-[10px] mt-0.5',
                                holiday ? 'border-red-300 text-red-600' : 'border-gray-300 text-gray-600'
                              )}>
                                {holiday ? t('timesheet.legend.holiday', 'Holiday') : t('timesheet.legend.weekend', 'Weekend')}
                              </Badge>
                            )}
                            {leaveMap[dateStr] && leaveMap[dateStr].type !== 'holiday' && (
                              <button
                                type="button"
                                className="mt-1"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setAbsenceDetail({ date: dateStr, ...leaveMap[dateStr] });
                                }}
                              >
                                <Badge variant={leaveMap[dateStr].state === 'pending' ? 'outline' : 'secondary'} className="text-[10px]">
                                  {getLeaveBadgeLabel(leaveMap[dateStr])}
                                </Badge>
                              </button>
                            )}
                          </td>
                          <td className="px-3 py-2 border-r">
                            <div className="text-muted-foreground text-xs space-y-0.5">
                              {!weekend && !blockingHoliday && !isReadOnlyMode && !cannotAddEntries && (
                                <div>{t('timesheet.clickToAdd', 'Click to add')}</div>
                              )}
                              {(weekend || blockingHoliday || isReadOnlyMode || cannotAddEntries) && <div>-</div>}
                            </div>
                          </td>
                          <td className="px-3 py-2 border-r text-center">-</td>
                          <td className="px-3 py-2 border-r text-center">-</td>
                          <td className="px-3 py-2 border-r text-center">-</td>
                          <td className="px-3 py-2 border-r text-center">
                            {leaveHours > 0 ? formatDuration(leaveHours) : '-'}
                          </td>
                          <td className={cn('px-3 py-2 border-r text-center', 'bg-orange-50 dark:bg-orange-900/30')}>
                            {leaveHours > 0 ? formatDuration(leaveHours) : '-'}
                          </td>
                          <td className="px-3 py-2 text-center" />
                        </tr>
                      );
                    }

                    // Day with entries — collapsible parent + sub-rows
                    return (
                      <React.Fragment key={dateStr}>
                        {/* Parent summary row */}
                        <tr
                          className={cn(
                            baseRowClass,
                            'cursor-pointer',
                            hasAnyEntry && 'bg-slate-50 dark:bg-slate-800/60',
                          )}
                          onClick={() => toggleDayExpanded(dateStr)}
                        >
                          <td className="sticky left-0 z-10 px-3 py-2 border-r bg-slate-50 dark:bg-slate-900 font-semibold">
                            {dayNumber}
                          </td>
                          <td className="px-3 py-2 border-r">
                            <div className="flex items-center gap-1">
                              <ChevronDown className={cn(
                                'h-3.5 w-3.5 text-muted-foreground transition-transform',
                                !isExpanded && '-rotate-90'
                              )} />
                              <div>
                                <div className="font-medium">{getDayName(date, true)}</div>
                                {(holiday || weekend) && (
                                  <Badge variant="outline" className={cn(
                                    'text-[10px] mt-0.5',
                                    holiday ? 'border-red-300 text-red-600' : 'border-gray-300 text-gray-600'
                                  )}>
                                    {holiday ? t('timesheet.legend.holiday', 'Holiday') : t('timesheet.legend.weekend', 'Weekend')}
                                  </Badge>
                                )}
                                {leaveMap[dateStr] && leaveMap[dateStr].type !== 'holiday' && (
                                  <button
                                    type="button"
                                    className="mt-1"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setAbsenceDetail({ date: dateStr, ...leaveMap[dateStr] });
                                    }}
                                  >
                                    <Badge variant={leaveMap[dateStr].state === 'pending' ? 'outline' : 'secondary'} className="text-[10px]">
                                      {getLeaveBadgeLabel(leaveMap[dateStr])}
                                    </Badge>
                                  </button>
                                )}
                                {dailyExpenseTotals[dateStr] > 0 && (
                                  <div className="flex items-center gap-0.5 mt-0.5">
                                    <Receipt className="h-2.5 w-2.5 text-amber-500" />
                                    <span className="text-[10px] text-amber-600 font-medium">
                                      EUR {dailyExpenseTotals[dateStr].toFixed(2)}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2 border-r">
                            <Badge variant="secondary" className="text-xs">
                              {dayEntries.length} {dayEntries.length === 1 ? t('timesheet.order', 'order') : t('timesheet.orders', 'orders')}
                            </Badge>
                          </td>
                          <td className={cn(
                            'px-3 py-2 border-r text-center font-bold',
                            dayWorkedTotal > 8 && 'text-red-600'
                          )}>
                            {formatDuration(dayWorkedTotal)}
                          </td>
                          <td className="px-3 py-2 border-r text-center font-semibold">
                            {daySummary.isOnCall ? <OnCallHours hours={dayOnCallTotal} /> : '-'}
                          </td>
                          <td className="px-3 py-2 border-r text-center font-semibold">
                            {dayOvertimeTotal > 0 ? formatDuration(dayOvertimeTotal) : '-'}
                          </td>
                          <td className="px-3 py-2 border-r text-center font-semibold">
                            {dayWorkPermitTotal > 0 ? formatDuration(dayWorkPermitTotal) : '-'}
                          </td>
                          <td className={cn(
                            'px-3 py-2 border-r text-center font-bold',
                            'bg-orange-50 dark:bg-orange-900/30',
                            dayWorkedTotal > 8 && 'text-red-600'
                          )}>
                            {formatDuration(dayTotal)}
                          </td>
                          <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                            {/* Show checkboxes for all draft entries in this day */}
                            {dayEntries.length === 1 && dayEntries[0]?.id && (!dayEntries[0].state || dayEntries[0].state === 'draft') && (
                              <Checkbox
                                checked={selectedEntryIds.has(dayEntries[0].id)}
                                onCheckedChange={() => toggleEntrySelection(dayEntries[0].id!)}
                              />
                            )}
                            {dayEntries.length === 1 && dayEntries[0]?.state && dayEntries[0].state !== 'draft' && (
                              <ApprovalStatusBadge state={dayEntries[0].state} compact />
                            )}
                          </td>
                        </tr>

                        {/* Expanded sub-rows */}
                        {isExpanded && (
                          <>
                            {dayEntries.map((entry) => {
                              const key = makeEntryKey(dateStr, entry.orderId);
                              const isModified = key in unsavedEntries;
                              const editable = isEntryEditable(entry);
                              const order = availableOrders.find(a => a.orderId === entry.orderId)?.order;
                              const entryTotal = sumHours([entry.workedHours, entry.onCallHours, entry.overtimeHours]);

                              return (
                                <tr
                                  key={key}
                                  data-entry-key={key}
                                  className={cn(
                                    'border-t border-dashed hover:bg-muted/50 cursor-pointer transition-colors',
                                    'bg-white dark:bg-slate-950',
                                    isModified && 'ring-1 ring-inset ring-orange-400',
                                    saveErrorEntryKeys.has(key) && 'ring-2 ring-inset ring-red-500 bg-red-50/40 dark:bg-red-950/40'
                                  )}
                                  onClick={() => !isReadOnlyMode && editable && handleCellClick(dateStr, entry.orderId)}
                                >
                                  <td className="sticky left-0 z-10 px-3 py-1 border-r bg-white dark:bg-slate-950" />
                                  <td className="px-3 py-1 border-r text-xs text-muted-foreground">↳</td>
                                  <td className="px-3 py-1 border-r">
                                    <div className="font-mono text-xs">{order?.code}</div>
                                    <div className="text-[10px] text-muted-foreground truncate max-w-[140px]">{order?.description}</div>
                                  </td>
                                  <td className="px-3 py-1 border-r text-center text-sm">
                                    {Number(entry.workedHours) ? formatDuration(entry.workedHours) : '-'}
                                  </td>
                                  <td className="px-3 py-1 border-r text-center text-sm">
                                    {isOnCall(entry) ? <OnCallHours hours={entry.onCallHours} /> : '-'}
                                  </td>
                                  <td className="px-3 py-1 border-r text-center text-sm">
                                    {Number(entry.overtimeHours) ? formatDuration(entry.overtimeHours) : '-'}
                                  </td>
                                  <td className="px-3 py-1 border-r text-center text-sm">
                                    {Number(entry.workPermit) ? formatDuration(entry.workPermit) : '-'}
                                  </td>
                                  <td className="px-3 py-1 border-r text-center text-xs text-muted-foreground bg-orange-50 dark:bg-orange-900/20">
                                    {formatDuration(entryTotal)}
                                  </td>
                                  <td className="px-3 py-1 text-center" onClick={(e) => e.stopPropagation()}>
                                    {entry.id && (!entry.state || entry.state === 'draft') && (
                                      <Checkbox
                                        checked={selectedEntryIds.has(entry.id)}
                                        onCheckedChange={() => toggleEntrySelection(entry.id!)}
                                      />
                                    )}
                                    {entry.state && entry.state !== 'draft' && (
                                      <ApprovalStatusBadge state={entry.state} compact />
                                    )}
                                  </td>
                                </tr>
                              );
                            })}

                            {/* + Add order button row */}
                            {canAddMore && !isReadOnlyMode && (
                              <tr
                                className="border-t border-dashed hover:bg-blue-50 dark:hover:bg-blue-950/30 cursor-pointer transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddOrderToDay(dateStr);
                                }}
                              >
                                <td className="sticky left-0 z-10 px-3 py-1 border-r bg-white dark:bg-slate-950" />
                                <td className="px-3 py-1 border-r" />
                                <td colSpan={7} className="px-3 py-1.5">
                                  <div className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 font-medium">
                                    <Plus className="h-3.5 w-3.5" />
                                    {t('timesheet.addOrder', 'Add order')}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {/* Monthly Totals Row */}
                  <tr className="border-t-2 bg-slate-200 dark:bg-slate-700 font-bold">
                    <td className="sticky left-0 z-10 px-3 py-3 border-r bg-slate-200 dark:bg-slate-700" colSpan={3}>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {t('timesheet.monthlyTotal', 'Monthly Total')}
                      </div>
                    </td>
                    <td className="px-3 py-3 border-r text-center text-lg">
                      {formatDuration(grandTotals.worked)} h
                    </td>
                    <td className="px-3 py-3 border-r text-center">
                      {formatDuration(grandTotals.onCall)} h
                    </td>
                    <td className="px-3 py-3 border-r text-center">
                      {formatDuration(grandTotals.overtime)} h
                    </td>
                    <td className="px-3 py-3 border-r text-center">
                      {formatDuration(monthlyPermitTotal)} h
                    </td>
                    <td className="px-3 py-3 border-r text-center text-lg bg-orange-200 dark:bg-orange-800/50">
                      {formatDuration(sumHours([grandTotals.worked, grandTotals.onCall, grandTotals.overtime, monthlyPermitTotal]))} h
                    </td>
                    <td className="px-3 py-3 text-center">
                      {/* Empty */}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Mobile view - day-by-day card layout for better touchscreen UX
  const renderMobileView = () => (
    <div className="space-y-3">
      {daysInMonth.map((date) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const dayEntries = entriesByDate[dateStr] || [];
        const holiday = isHoliday(date);
        const blockingHoliday = isBlockingHoliday(date);
        const weekend = isWeekend(date);
        const dayNumber = format(date, 'd');
        const leaveHours = getEffectiveLeaveHoursForDate(dateStr);

        // Calculate day totals
        const dayTotal = sumHours([
          ...dayEntries.flatMap((e) => [e.workedHours, e.onCallHours, e.overtimeHours, e.workPermit]),
          leaveHours,
        ]);
        const hasAnyEntry = dayEntries.length > 0;
        const addDisabled = Boolean(isReadOnlyMode || cannotAddEntries || weekend || blockingHoliday);

        return (
          <Card key={dateStr} className={cn(
            holiday && 'border-red-300 bg-red-50 dark:bg-red-950/30',
            weekend && !holiday && 'border-gray-300 bg-gray-50 dark:bg-gray-900/30'
          )}>
            <CardContent className="p-3">
              {/* Date Header */}
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-semibold text-sm">{getDayName(date, false)}</div>
                  <div className="text-xs text-muted-foreground">{dayNumber} {format(date, 'MMM', { locale: dateLocale })}</div>
                  {leaveMap[dateStr] && leaveMap[dateStr].type !== 'holiday' && (
                    <button
                      type="button"
                      className="mt-1"
                      onClick={() =>
                        setAbsenceDetail({ date: dateStr, ...leaveMap[dateStr] })
                      }
                    >
                      <Badge variant={leaveMap[dateStr].state === 'pending' ? 'outline' : 'secondary'} className="text-[10px]">
                        {getLeaveBadgeLabel(leaveMap[dateStr])}
                      </Badge>
                    </button>
                  )}
                </div>
                <div className="text-right">
                  {(holiday || weekend) && (
                    <Badge variant="outline" className={cn(
                      'text-[10px]',
                      holiday ? 'border-red-300 text-red-600' : 'border-gray-300 text-gray-600'
                    )}>
                      {holiday ? t('timesheet.legend.holiday', 'Holiday') : t('timesheet.legend.weekend', 'Weekend')}
                    </Badge>
                  )}
                  {hasAnyEntry && (
                    <div className="text-sm font-semibold text-orange-600 dark:text-orange-400 mt-1">
                      {formatDuration(dayTotal)} h
                    </div>
                  )}
                </div>
              </div>

              {/* Entries or placeholder */}
              {hasAnyEntry ? (
                <div className="space-y-2">
                  {dayEntries.map((entry) => {
                    const key = makeEntryKey(dateStr, entry.orderId);
                    const isModified = key in unsavedEntries;
                    const editable = isEntryEditable(entry);
                    const order = availableOrders.find(a => a.orderId === entry.orderId)?.order;

                    return (
                      <div
                        key={key}
                        className={cn(
                          'p-2 rounded border text-xs space-y-1 cursor-pointer',
                          isModified && 'border-orange-400 bg-orange-50 dark:bg-orange-900/20',
                          !editable && 'bg-gray-50 dark:bg-gray-800'
                        )}
                        onClick={() => !isReadOnlyMode && editable && handleCellClick(dateStr, entry.orderId)}
                      >
                        <div className="font-mono font-medium">{order?.code}</div>
                        <div className="text-muted-foreground flex justify-between">
                          <span>{order?.description}</span>
                          <span className="font-semibold">
                            {formatDuration(sumHours([entry.workedHours, entry.onCallHours, entry.overtimeHours]))} h
                          </span>
                        </div>
                        <EntryHoursBreakdown entry={entry} />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <button
                  type="button"
                  disabled={addDisabled}
                  onClick={() => {
                    if (addDisabled) return;
                    setSelectedCell({ date: dateStr, orderId: availableOrders[0].orderId });
                    setModalMode('add');
                    setModalOpen(true);
                  }}
                  className={cn(
                    'w-full py-2 text-xs rounded border-2 border-dashed transition-colors',
                    addDisabled ? 'text-muted-foreground cursor-not-allowed' : 'text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer'
                  )}
                >
                  {addDisabled ? '-' : t('timesheet.clickToAdd', 'Tap to add')}
                </button>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  const monthTitle = format(selectedDate, 'MMMM yyyy', { locale: dateLocale });

  return (
    <PageTransition>
      <div className="p-4 sm:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-900">
              <Calendar className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h1 className="text-xl font-semibold">{t('timesheet.title')}</h1>
              <p className="text-sm text-muted-foreground">{monthTitle}</p>
            </div>
          </div>

          <div className="flex w-full items-center justify-end gap-2 sm:w-auto">
            <Button
              variant="outline"
              onClick={handleDownloadMonthlyPdf}
              disabled={loading || downloadingPdf}
              className="gap-2"
            >
              {downloadingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              <span>{t('timesheet.actions.exportPdf', 'Esporta PDF')}</span>
            </Button>
            <Button variant="outline" size="icon" onClick={() => navigateMonth('prev')}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => setSelectedDate(new Date())}
              className="min-w-[120px]"
              data-testid="month-selector"
            >
              {format(selectedDate, 'MMM yyyy', { locale: dateLocale })}
            </Button>
            <Button variant="outline" size="icon" onClick={() => navigateMonth('next')}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Card>
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-orange-500" />
                <span className="text-sm text-muted-foreground">{t('timesheet.columns.workedHours')}</span>
              </div>
              <div className="text-2xl font-bold mt-1">{formatDuration(grandTotals.worked)} h</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-blue-500" />
                <span className="text-sm text-muted-foreground">{t('timesheet.columns.onCallHours')}</span>
              </div>
              <div className="text-2xl font-bold mt-1">{formatDuration(grandTotals.onCall)} h</div>
              {onCallDaysCount > 0 && (
                <div className="text-xs text-muted-foreground">
                  {t('timesheet.summary.onCallDays', '{{count}} days on call', { count: onCallDaysCount })}
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <Timer className="h-4 w-4 text-purple-500" />
                <span className="text-sm text-muted-foreground">{t('timesheet.columns.overtimeHours')}</span>
              </div>
              <div className="text-2xl font-bold mt-1">{formatDuration(grandTotals.overtime)} h</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-green-500" />
                <span className="text-sm text-muted-foreground">{t('timesheet.summary.absences', 'Assenze')}</span>
              </div>
              <div className="text-2xl font-bold mt-1">{formatDuration(absenceStats.totalHours)} h</div>
            </CardContent>
          </Card>
          {monthlyExpenseClaims.length > 0 && (
            <Card>
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <Receipt className="h-4 w-4 text-amber-500" />
                  <span className="text-sm text-muted-foreground">{t('expenseClaims.title', 'Note spese')}</span>
                </div>
                <div className="text-2xl font-bold mt-1">EUR {monthlyExpenseTotal.toFixed(2)}</div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-green-500" />
                <span className="text-sm text-muted-foreground">{t('timesheet.daysWorked', 'Days')}</span>
              </div>
              <div className="text-2xl font-bold mt-1">
                {Object.keys(dailyTotals).filter(d => dailyTotals[d] > 0).length}
              </div>
            </CardContent>
          </Card>
          </div>

          {(absenceStats.vacationHours > 0
            || absenceStats.permissionHours > 0
            || absenceStats.sicknessHours > 0
            || absenceStats.rolHours > 0) && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {absenceStats.vacationHours > 0 && (
                <Card>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-cyan-600" />
                      <span className="text-sm text-muted-foreground">{t('leave.types.vacation', 'Ferie')}</span>
                    </div>
                    <div className="text-2xl font-bold mt-1">{formatDuration(absenceStats.vacationHours)} h</div>
                  </CardContent>
                </Card>
              )}

              {absenceStats.permissionHours > 0 && (
                <Card>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <CheckSquare className="h-4 w-4 text-indigo-600" />
                      <span className="text-sm text-muted-foreground">{t('leave.types.permission', 'Permesso')}</span>
                    </div>
                    <div className="text-2xl font-bold mt-1">{formatDuration(absenceStats.permissionHours)} h</div>
                  </CardContent>
                </Card>
              )}

              {absenceStats.sicknessHours > 0 && (
                <Card>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <Timer className="h-4 w-4 text-red-500" />
                      <span className="text-sm text-muted-foreground">{t('leave.types.sickness', 'Malattia')}</span>
                    </div>
                    <div className="text-2xl font-bold mt-1">{formatDuration(absenceStats.sicknessHours)} h</div>
                  </CardContent>
                </Card>
              )}

              {absenceStats.rolHours > 0 && (
                <Card>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-amber-600" />
                      <span className="text-sm text-muted-foreground">{t('leave.types.rol', 'ROL')}</span>
                    </div>
                    <div className="text-2xl font-bold mt-1">{formatDuration(absenceStats.rolHours)} h</div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>

        {/* Actions Bar */}
        <div className="flex flex-wrap items-center gap-2">
          {isReadOnlyMode && (
            <Badge variant="outline" className="border-blue-300 text-blue-700 bg-blue-50">
              {t('timesheet.readOnlyApproved', 'Approved month - read-only')}
            </Badge>
          )}

          {!isReadOnlyMode && draftEntries.length > 0 && (
            <Button
              variant="outline"
              onClick={toggleAllDrafts}
              disabled={saving || submitting}
              className="gap-2"
            >
              {allDraftsSelected ? (
                <CheckSquare className="h-4 w-4" />
              ) : (
                <Square className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">
                {t('timesheet.actions.selectAllDraft', 'Select All Draft')}
              </span>
            </Button>
          )}

          {!isReadOnlyMode && availableOrders.length > 0 && (
            <Button
              variant="outline"
              onClick={() => setAutoFillOpen(true)}
              disabled={saving || submitting}
              className="gap-2"
            >
              <Wand2 className="h-4 w-4" />
              <span className="hidden sm:inline">
                {t('timesheet.actions.autoFill', 'Auto-Fill Month')}
              </span>
            </Button>
          )}

          <div className="flex-1" />

          {selectedEntryIds.size > 0 && (
            <Badge variant="outline" className="border-blue-300 text-blue-600">
              {selectedEntryIds.size} {t('approval.selected', 'selected')}
            </Badge>
          )}

          {!isReadOnlyMode && hasUnsavedChanges && (
            <Badge variant="outline" className="border-orange-300 text-orange-600" data-testid="unsaved-changes-badge">
              {Object.keys(unsavedEntries).length} {t('timesheet.unsavedChanges', 'unsaved')}
            </Badge>
          )}

          <Button
            onClick={handleSaveAll}
            disabled={saving || submitting || deleting || !hasUnsavedChanges || isReadOnlyMode}
            className={cn('gap-2 bg-orange-500 hover:bg-orange-600', isReadOnlyMode && 'hidden')}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{saving ? t('common.saving') : t('common.saveAll')}</span>
          </Button>

          {!isReadOnlyMode && selectedEntryIds.size > 0 && (
            <Button
              variant="outline"
              onClick={handleDeleteSelected}
              disabled={saving || submitting || deleting}
              className="gap-2 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              <span>{deleting ? t('timesheet.actions.deleting', 'Deleting...') : t('timesheet.actions.delete', 'Delete')}</span>
            </Button>
          )}

          <Button
            onClick={handleSubmitForApproval}
            disabled={
              saving ||
              submitting ||
              deleting ||
              hasUnsavedChanges ||
              selectedEntryIds.size === 0 ||
              monthIsApproved
            }
            className="gap-2 bg-blue-500 hover:bg-blue-600"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            <span>{t('timesheet.actions.submit', 'Submit')}</span>
          </Button>

          {monthIsApproved && (
            <ApprovalStatusBadge state="approved" compact />
          )}
        </div>



        {ordersLoadFailed && (
          <Card className="border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/30" data-testid="orders-load-failed-banner">
            <CardContent className="flex items-start gap-3 p-4">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-red-900 dark:text-red-200">
                  {t('timesheet.errors.ordersLoadFailed', 'Could not load your orders')}
                </p>
                <p className="text-sm text-red-800 dark:text-red-300">
                  {t('timesheet.errors.ordersLoadFailedHint', 'Cannot reach the server. Reload the page; if it persists, report it to your system administrator.')}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {hasNoAssignedOrders && (
          <Card className="border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30" data-testid="no-orders-banner">
            <CardContent className="flex items-start gap-3 p-4">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                  {t('timesheet.noOrdersAssigned', 'No orders assigned')}
                </p>
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  {t('timesheet.errors.contactManager', 'Contact your manager to get assigned to orders.')}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Grid/Table */}
        {isMobile ? renderMobileView() : renderGridView()}

        {/* Legend */}
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-wrap gap-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded bg-red-100 dark:bg-red-950/50" />
                <span>{t('timesheet.legend.holiday', 'Holiday')}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded bg-gray-100 dark:bg-gray-800" />
                <span>{t('timesheet.legend.weekend', 'Weekend')}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded ring-1 ring-orange-400" />
                <span>{t('timesheet.legend.unsaved', 'Unsaved')}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded ring-2 ring-red-500" />
                <span>{t('timesheet.legend.saveError', 'Invalid entry to fix')}</span>
              </div>
              <div className="flex items-center gap-1">
                <Phone className="h-3 w-3 text-blue-500" />
                <span>{t('timesheet.legend.hasExtra', 'Has on-call/overtime (hover for details)')}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Entry Edit Modal */}
        <DateRangeEntryDialog
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedCell(null);
          }}
          onSubmit={handleModalSubmit}
          onDelete={handleModalDelete}
          initialDate={selectedCell?.date}
          initialOrderId={selectedCell?.orderId}
          initialEntry={dialogInitialEntry}
          entryState={getSelectedCellEntry()?.state}
          mode={modalMode}
          otherEntriesWorkedHours={
            selectedCell
              ? (entriesByDate[selectedCell.date] || [])
                  .filter((e) => e.orderId !== selectedCell.orderId || modalMode === 'add')
                  .reduce((sum, e) => sum + Number(e.workedHours || 0), 0)
              : 0
          }
          usedOrderIds={
            selectedCell
              ? (entriesByDate[selectedCell.date] || []).map((e) => e.orderId)
              : []
          }
          approvedLeaveHours={
            selectedCell
              ? getEffectiveLeaveHoursForDate(selectedCell.date)
              : 0
          }
        />

        {/* Auto-Fill Month Modal */}
        <AutoFillDialog
          open={autoFillOpen}
          onClose={() => setAutoFillOpen(false)}
          onConfirm={handleAutoFill}
          orders={availableOrders
            .filter((a) => a.order)
            .map((a) => ({
              orderId: a.orderId,
              code: a.order!.code,
              description: a.order!.description,
            }))}
        />

        <Dialog open={absenceDetail !== null} onOpenChange={(open) => !open && setAbsenceDetail(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('timesheet.absenceDetail.title', 'Dettaglio assenza')}</DialogTitle>
              <DialogDescription>{absenceDetail?.date}</DialogDescription>
            </DialogHeader>

            {absenceDetail && (
              <div className="space-y-2 text-sm">
                <p>
                  <span className="font-medium">{t('timesheet.absenceDetail.type', 'Tipo')}:</span>{' '}
                  {getLeaveTypeLabel(absenceDetail.type)}
                </p>
                {absenceDetail.state && (
                  <p>
                    <span className="font-medium">{t('timesheet.absenceDetail.state', 'Stato')}:</span>{' '}
                    {t(`leave.state.${absenceDetail.state}`, absenceDetail.state)}
                  </p>
                )}
                <p>
                  <span className="font-medium">{t('timesheet.absenceDetail.duration', 'Durata')}:</span>{' '}
                  {getLeaveDurationLabel(absenceDetail.durationType)}
                </p>
                {absenceDetail.durationType === 'hours' && typeof absenceDetail.hours === 'number' && (
                  <p>
                    <span className="font-medium">{t('timesheet.absenceDetail.hours', 'Ore')}:</span> {absenceDetail.hours}
                  </p>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setAbsenceDetail(null)}>
                {t('common.close', 'Chiudi')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PageTransition>
  );
};

export default MonthlyTimesheet;
